import React, { useState } from "react"
import {
  BadgeTemplate, PrinterModel, PrinterMedia,
} from "sharedTypes/badge"
import Editor from "components/CanvasEditor/Editor"
import { EditorContent, EditorPanel } from "components/CanvasEditor/EditorLayout"
import {
  EditorToolbar, EditorToolbarSeparator,
} from "components/CanvasEditor/Toolbar/EditorToolbar"
import ToolbarHistory from "components/CanvasEditor/Toolbar/ToolbarHistory"
import ToolbarZoom from "components/CanvasEditor/Toolbar/ToolbarZoom"
import { BadgeUIProvider } from "./contexts/BadgeUIContext"
import BadgeBuilderProvider from "./BadgeBuilderContext"
import { EventData, GuestData } from "./utils/badgeDataResolver"
import LayersPanel from "./Panels/LayersPanel/LayersPanel"
import ElementPanel from "./Panels/EditorPanel/EditorPanel"
import BadgeCanvas from "./BadgeCanvas/BadgeCanvas"
import BadgeAddElements from "./BadgeAddElements"
import getInitialTemplate from "./utils/initialTemplate"
import BADGE_KEYBINDINGS from "./keybindings"
import BadgeShortcuts from "./BadgeShortcuts"
import { EDITOR_RESOLUTION } from "./utils/constants"

type Props = {
  template?: BadgeTemplate
  selectedPrinterModel?: PrinterModel
  selectedPrinterMedia?: PrinterMedia
  onTemplateChange: (template: BadgeTemplate) => void
  guestData?: GuestData
  eventData?: EventData
}

const ZOOM = { min: 0.25, max: 4, step: 1.2 }

/**
 * Edits a badge template. It starts from `template` and reports each change the user makes
 * through `onTemplateChange`. To load another template, remount it with a new `key`.
 */
const BadgeBuilder = ({
  template,
  selectedPrinterModel,
  selectedPrinterMedia,
  onTemplateChange,
  guestData,
  eventData,
}: Props) => {
  const [initialTemplate] = useState(
    () => getInitialTemplate(template, selectedPrinterModel, selectedPrinterMedia),
  )

  return (
    <Editor
      zoom={ZOOM}
      scale={EDITOR_RESOLUTION}
      defaultValue={initialTemplate}
      onChange={onTemplateChange}
      keybindings={BADGE_KEYBINDINGS}
    >
      <BadgeUIProvider>
        <BadgeBuilderProvider guestData={guestData} eventData={eventData}>
          <BadgeBuilderContent />
        </BadgeBuilderProvider>
      </BadgeUIProvider>
    </Editor>
  )
}

const BadgeBuilderContent = () => (
  <EditorContent>
    <BadgeShortcuts />
    <EditorPanel side="left" width={220}>
      <LayersPanel />
    </EditorPanel>
    <BadgeCanvas />
    <EditorPanel side="right" width={300}>
      <ElementPanel />
    </EditorPanel>
    <EditorToolbar>
      <ToolbarHistory />
      <EditorToolbarSeparator />
      <BadgeAddElements group="shapes" />
      <EditorToolbarSeparator invisible />
      <BadgeAddElements group="elements" />
      <EditorToolbarSeparator />
      <ToolbarZoom />
    </EditorToolbar>
  </EditorContent>
)

// The page re-renders on every change it's told about; the editor already has it
export default React.memo(BadgeBuilder)
